import { JOURNAL_MAX_BYTES, JOURNAL_MAX_EVENTS, JOURNAL_STORAGE_KEY } from './config';
import { sendEvent } from './transport';
import type { EventIdentity, TrackedEvent } from './types';

// Single module-scope encoder (stateless) avoids re-allocating per cap pass.
const encoder = new TextEncoder();

const byteLength = (value: string): number => encoder.encode(value).length;

const eventByteLength = (event: TrackedEvent): number => byteLength(JSON.stringify(event));

// Serialized array byte length from element bytes + count, without re-stringifying:
// JSON.stringify(arr) = '[' + join(',') + ']' (all structural chars single-byte).
// Empty → 0 (callers treat empty as zero, not the literal '[]').
const arrayByteLength = (elemBytes: number, count: number): number => (count === 0 ? 0 : elemBytes + count + 1);

// Guard against foreign writes to the journal key: require an event_type. Extra
// keys are tolerated so a legacy entry (old batched shape carried an `identity`
// block) still replays — transport ignores it, reading identity from its param.
const isEventShape = (value: unknown): value is TrackedEvent =>
    value !== null && typeof value === 'object' && 'event_type' in value;

// Safe read: try/catch, JSON.parse, shape-filter, default [] (mirrors
// getEntrySignals in src/analytics/recommendations.ts).
const readJournal = (): TrackedEvent[] => {
    try {
        const raw = localStorage.getItem(JOURNAL_STORAGE_KEY);
        if (!raw) return [];
        const data = JSON.parse(raw);
        if (!Array.isArray(data)) return [];
        return data.filter(isEventShape);
    } catch (error) {
        console.error('sbTracker: journal read failed', error);
        return [];
    }
};

const writeJournal = (events: TrackedEvent[]): void => {
    try {
        localStorage.setItem(JOURNAL_STORAGE_KEY, JSON.stringify(events));
    } catch (error) {
        console.error('sbTracker: journal write failed', error);
    }
};

const clearJournal = (): void => {
    try {
        localStorage.removeItem(JOURNAL_STORAGE_KEY);
    } catch (error) {
        console.error('sbTracker: journal clear failed', error);
    }
};

// Evict oldest (FIFO) until both the event-count and byte caps hold.
const enforceCaps = (events: TrackedEvent[]): TrackedEvent[] => {
    const capped = [...events];
    // Serialize element bytes once, decrement per evict — O(n), no re-serialize.
    const elemBytes = capped.map(eventByteLength);
    let totalElemBytes = elemBytes.reduce((sum, n) => sum + n, 0);
    while (
        capped.length > 0 &&
        (capped.length > JOURNAL_MAX_EVENTS || arrayByteLength(totalElemBytes, capped.length) > JOURNAL_MAX_BYTES)
    ) {
        capped.shift();
        totalElemBytes -= elemBytes.shift() ?? 0;
    }
    return capped;
};

// Send one event now; on transport refusal, journal it (append + enforce caps).
// Identity is passed through, not stored — the journal carries no identity block.
export const sendOne = (event: TrackedEvent, identity: EventIdentity): void => {
    const accepted = sendEvent(event, identity);
    if (accepted) return;

    writeJournal(enforceCaps([...readJournal(), event]));
};

/**
 * On init, replay journalled events (from a prior page's send refusal), one
 * request each, with the *current* identity — replayed events carry the
 * replaying page's session/user (accepted: session is a rolling 30-min window,
 * user_id is current-auth). Legacy entries' stale `identity` block is ignored.
 * Refused entries are kept; the journal is rewritten to exactly those.
 */
export const drainJournal = (identity: EventIdentity): void => {
    const journal = readJournal();
    if (journal.length === 0) return;

    const refused: TrackedEvent[] = [];
    for (const entry of journal) {
        // Fire-and-forget: "accepted" = handed to transport (returns true before settle).
        if (!sendEvent(entry, identity)) {
            refused.push(entry);
        }
    }

    if (refused.length === 0) {
        clearJournal();
    } else {
        writeJournal(refused);
    }
};
